import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DateRange as DateRangeType } from 'rsuite/esm/DateRangePicker';

/**
 * Hook for managing quotes filters with URL synchronization
 * Following the orders module pattern
 */
export const useQuotesFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const updateSearchParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(prevParams => {
        const newParams = new URLSearchParams(prevParams);

        Object.entries(updates).forEach(([key, value]) => {
          if (!value) {
            newParams.delete(key);
          } else {
            newParams.set(key, value);
          }
        });

        return newParams;
      });
    },
    [setSearchParams]
  );

  const handleCustomerSelect = useCallback(
    (customer: {
      accountNumber: string | null;
      name: string | null;
      _id: string | null;
    }) => {
      const accountNumber = customer?.accountNumber || null;
      const accountName = customer?.name || null;
      const accountId = customer?._id || null;

      updateSearchParams({
        customerAccountNumber: accountNumber,
        accountName,
        accountId
      });
    },
    [updateSearchParams]
  );

  const handleStatusSelect = useCallback(
    (statusId: string | null) => {
      updateSearchParams({ statusId });
    },
    [updateSearchParams]
  );

  const handleDateRangeChange = useCallback(
    (dateRange: DateRangeType | null) => {
      updateSearchParams({
        date: dateRange
          ? `${dateRange[0].toISOString()},${dateRange[1].toISOString()}`
          : null
      });
    },
    [updateSearchParams]
  );

  const handleSearch = useCallback(
    (searchStr: string) => {
      updateSearchParams({ searchStr: searchStr || '' });
    },
    [updateSearchParams]
  );

  return {
    searchParams,
    handleCustomerSelect,
    handleStatusSelect,
    handleDateRangeChange,
    handleSearch
  };
};
